import {
  createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode,
} from "react";

export type Period = "12m" | "6m" | "3m" | "1m";

export type FilterValues = Record<string, string[]>;

export interface DashboardState {
  page: string;
  period: Period;
  store?: string;
  start_month?: string;
  end_month?: string;
  filters: FilterValues;
  theme: "light" | "dark";
  selections: string[];
  focus?: Record<string, any>;
}

export interface DashboardPatch {
  page?: string;
  period?: Period;
  store?: string;
  start_month?: string;
  end_month?: string;
  filters?: FilterValues;
  theme?: "light" | "dark";
  selections?: string[];
  focus?: Record<string, any>;
}

interface Ctx {
  filters: DashboardState;
  filterKeys: string[];
  ready: number;
  setFilterKeys: (keys: string[]) => void;
  setPeriod: (p: Period) => void;
  setPage: (p: string) => void;
  setStore: (s: string | undefined) => void;
  setFilterValue: (id: string, values: string[]) => void;
  clearFilters: () => void;
  applyPatch: (patch: DashboardPatch) => void;
  markReady: () => void;
  reset: () => void;
}

const DEFAULT: DashboardState = {
  page: "resumo",
  period: "12m",
  store: undefined,
  start_month: undefined,
  end_month: undefined,
  filters: {},
  theme: "light",
  selections: [],
  focus: undefined,
};

const C = createContext<Ctx>({
  filters: DEFAULT,
  filterKeys: [],
  ready: 0,
  setFilterKeys: () => {},
  setPeriod: () => {},
  setPage: () => {},
  setStore: () => {},
  setFilterValue: () => {},
  clearFilters: () => {},
  applyPatch: () => {},
  markReady: () => {},
  reset: () => {},
});

function normalizeFilters(keys: string[], candidate: FilterValues): FilterValues {
  if (!keys.length) return { ...candidate };
  const out: FilterValues = {};
  for (const k of keys) {
    const v = candidate[k];
    if (Array.isArray(v) && v.length > 0) out[k] = v.slice();
  }
  return out;
}
function hasActiveFilters(f: FilterValues): boolean {
  return Object.values(f).some((v) => Array.isArray(v) && v.length > 0);
}

export function DashboardFiltersProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<DashboardState>(DEFAULT);
  const [filterKeys, setFilterKeys] = useState<string[]>([]);
  const [ready, setReady] = useState(0);
  const filterKeysRef = useRef<string[]>([]);
  filterKeysRef.current = filterKeys;

  const applyPatch = useCallback((patch: DashboardPatch) => {
    if (!patch || typeof patch !== "object") return;
    setFilters((prev) => {
      const nextFilters = patch.filters
        ? normalizeFilters(filterKeysRef.current, { ...prev.filters, ...patch.filters })
        : prev.filters;
      const clearedAll = patch.filters !== undefined && !hasActiveFilters(nextFilters);
      const next: DashboardState = {
        ...prev,
        ...(patch.page !== undefined ? { page: String(patch.page) } : {}),
        ...(patch.period !== undefined ? { period: patch.period } : {}),
        ...(patch.store !== undefined ? { store: patch.store || undefined } : {}),
        ...(patch.start_month !== undefined ? { start_month: patch.start_month || undefined } : {}),
        ...(patch.end_month !== undefined ? { end_month: patch.end_month || undefined } : {}),
        ...(patch.theme !== undefined ? { theme: patch.theme } : {}),
        filters: nextFilters,
        selections: clearedAll ? [] : (patch.selections ?? prev.selections),
        focus: clearedAll ? undefined : (patch.focus ?? prev.focus),
      };
      return next;
    });
  }, []);

  // TODA funcao daqui precisa de identidade estavel.
  //
  // Medido em 04/11/2026: `markReady` nascia dentro do useMemo, entao mudava
  // de identidade a cada bump de `ready`. O DashboardViewer tinha um
  // `useEffect(..., [dataQ.data, dataQ.isFetching, markReady])` que chamava
  // markReady() — bump, novo value, nova identidade, efeito roda de novo,
  // bump. Laco infinito de render: a aba do dashboard travava o renderer, e
  // clicar em "Home" no menu nao fazia nada porque a thread principal nunca
  // voltava para processar o clique. So o F5 saia de la.
  //
  // Funcao de contexto e dependencia de efeito de quem consome. Se ela muda
  // de identidade a cada render, todo efeito que depende dela vira um laco.
  const setPeriod = useCallback((p: Period) => setFilters((f) => ({ ...f, period: p })), []);
  const setPage = useCallback((p: string) => setFilters((f) => ({ ...f, page: p })), []);
  const setStore = useCallback((s: string | undefined) => setFilters((f) => ({ ...f, store: s })), []);
  const setFilterValue = useCallback((id: string, values: string[]) => {
    setFilters((f) => {
      const merged = { ...f.filters, [id]: values.slice() };
      const normalized = normalizeFilters(filterKeysRef.current, merged);
      const clearedAll = !hasActiveFilters(normalized);
      return {
        ...f,
        filters: normalized,
        selections: clearedAll ? [] : f.selections,
        focus: clearedAll ? undefined : f.focus,
      };
    });
  }, []);
  const clearFilters = useCallback(
    () => setFilters((f) => ({ ...f, filters: {}, selections: [], focus: undefined })), []);
  const markReady = useCallback(() => setReady((r) => r + 1), []);
  const reset = useCallback(() => setFilters(DEFAULT), []);

  const value = useMemo<Ctx>(() => ({
    filters,
    filterKeys,
    ready,
    setFilterKeys,
    setPeriod,
    setPage,
    setStore,
    setFilterValue,
    clearFilters,
    applyPatch,
    markReady,
    reset,
  }), [
    filters, filterKeys, ready,
    setPeriod, setPage, setStore, setFilterValue, clearFilters, applyPatch,
    markReady, reset,
  ]);

  return <C.Provider value={value}>{children}</C.Provider>;
}

export function useDashboardFilters() {
  return useContext(C);
}
